import { useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, useBreakpoint } from '@factorialco/deprecated-design-system'

import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import ProcessTargetDataTable from 'modules/performance_process/components/overview/ProcessTargetDataTable'
import { EmployeeScoreChart } from 'modules/performance_process/components/review/show/EmployeeScoreChart'
import {
  Filters,
  useFilterManager,
} from 'modules/performance_process/components/review/show/Filters'
import {
  useFilter,
  useFiltersValues,
} from 'modules/performance_process/components/review/show/Filters/context'
import { FiltersWrapper } from 'modules/performance_process/components/review/show/FiltersWrapper'
import { TasksContainer } from 'modules/performance_process/components/review/show/Tasks'
import { ParticipationRateTask } from 'modules/performance_process/components/review/show/Tasks/ParticipationRate'
import { PeerAssignmentTask } from 'modules/performance_process/components/review/show/Tasks/PeerAssignment'
import { PendingAgreementsTask } from 'modules/performance_process/components/review/show/Tasks/PendingAgreements'
import { PendingEvaluationsTask } from 'modules/performance_process/components/review/show/Tasks/PendingEvaluations'
import { ReviewOverviewOutletContext } from 'modules/performance_process/pages/reviews/show/overview'

const ReviewOverviewInsightsPage = () => {
  const {
    reviewProcess,
    canOnlySeePersonalResults,
    materializedTargetIds,
    teams,
    locations,
    legalEntities,
  } = useOutletContext<ReviewOverviewOutletContext>()
  const { isMobile } = useBreakpoint()

  const filters = useFiltersValues()
  const { setValue: setScores } = useFilter('scores')
  const { setValue: setAgreementStatus } = useFilter('agreementStatus')

  const { search, debouncedSearch, onSearch } = useDebouncedSearch('', {
    delay: true,
    minimumCharacters: 2,
  })

  const hasEmployeeScores = !!reviewProcess.scoreConfiguration
  const scoreScale = reviewProcess.scoreScale.scale

  useEffect(() => {
    if (!reviewProcess.agreementsConfiguration.enabled && filters.agreementStatus !== null) {
      setAgreementStatus(null)
    }
  }, [reviewProcess.agreementsConfiguration.enabled, filters.agreementStatus, setAgreementStatus])

  const filterProps = useMemo(
    () => ({
      materializedTargetIds,
      teams,
      locations,
      legalEntities,
      scoreScale,
      hasEmployeeScores,
      excludeAgreementStatus: !reviewProcess.agreementsConfiguration.enabled,
    }),
    [
      materializedTargetIds,
      teams,
      locations,
      legalEntities,
      scoreScale,
      hasEmployeeScores,
      reviewProcess.agreementsConfiguration.enabled,
    ]
  )

  const { persistentFilters, conditionalFilters, availableFilters, onSelectFilter } =
    useFilterManager({
      configType: 'insights',
      filterProps,
    })

  if (canOnlySeePersonalResults) return null

  return (
    <>
      <FiltersWrapper>
        <Filters.FilterManager
          persistentFilters={persistentFilters}
          conditionalFilters={conditionalFilters}
          availableFilters={availableFilters}
          onSelectFilter={onSelectFilter}
          searchConfig={{
            search,
            onSearch,
            placeholder: i18n.t('performance_process.reviews.target_management_search'),
            collapsable: true,
          }}
        />
      </FiltersWrapper>

      <Box paddingTop="s48" gap="s16">
        <TasksContainer>
          <ParticipationRateTask search={debouncedSearch} />
          <PendingEvaluationsTask />
          <PendingAgreementsTask />
          <PeerAssignmentTask />
        </TasksContainer>

        {hasEmployeeScores && !isMobile && (
          <EmployeeScoreChart search={debouncedSearch} onChangeScores={setScores} />
        )}

        <ProcessTargetDataTable
          agreementEnabled={reviewProcess.agreementsConfiguration.enabled}
          showEmployeeScores={hasEmployeeScores}
          reviewId={reviewProcess.id}
          reviewerStrategies={reviewProcess.reviewerStrategies ?? []}
          managedByFilter={
            filters.managerId
              ? {
                  managerEmployeeId: filters.managerId,
                  onlyDirectReports: filters.onlyDirectReports ?? true,
                }
              : undefined
          }
          searchFilter={debouncedSearch}
          agreementCompletionStatusFilter={filters.agreementStatus ?? undefined}
          evaluationsCompletionStatusFilter={filters.evaluationsStatus ?? undefined}
          employeeIdsFilter={filters.employeeIds}
          scoresFilter={filters.scores}
          teamIdsFilter={filters.teamIds}
          tenureDatesFilter={filters.tenureDates}
          workplaceIdsFilter={filters.workplaceIds}
          legalEntityIdsFilter={filters.legalEntityIds}
        />
      </Box>
    </>
  )
}

export default ReviewOverviewInsightsPage
